/**
 * The billing cycle: the proration divisor, and the period boundary.
 *
 * These were once the same number (30) in two guises, and separating them is
 * what let the boundary move to calendar anniversaries. They are still three
 * different questions and must never be recombined:
 *
 *   - `PRORATION_CYCLE_DAYS` is the divisor of a report written BEFORE the
 *     anniversary change, when it was a hard 30. History, not arithmetic.
 *   - `billingPeriodEnd` answers where the period's BOUNDARY falls. Calendar.
 *   - `billingMonthDays` answers what a period is PRICED AGAINST: the length of
 *     the anniversary month it sits in.
 *
 * A period no longer has a constant length, which is why there is no
 * `PERIOD_LENGTH_DAYS` here any more: ask `billingPeriodEnd` instead. And a
 * period's own length is NOT its divisor — see `billingMonthDays` for why that
 * distinction is the whole point.
 */

/**
 * The divisor that turns a slice's day count into a fraction of a month's price.
 *
 * A PROPERTY OF HOW A PERIOD WAS PRICED — not of how long it lasted. This is
 * emphatically NOT the period's length: `partner/admin/billing-report.ts`
 * (`PRORATION_DAYS`) and `util/erp-csv.ts` both document, after real bugs, that
 * a period's real length is not guaranteed to be 30 — `period_end` is only
 * deterministically `start + 30 days` for rows written after that became the
 * rule, and older rows run longer. Dividing a slice by its period's REAL length
 * instead of by this fixed cycle misstates the day fraction, and is what made a
 * recovered EUR 22.99 seat rate read as EUR 23.76 (22.99 x 31/30). A
 * "seat-month" is a fixed unit precisely because this divisor never varies.
 */
export const PRORATION_CYCLE_DAYS = 30;

/**
 * THE ANCHOR — the day of the month an org bills on.
 *
 * Stored on `orgs.billing_anchor_day` (1–31, `NULL` until backfilled) and
 * STORED rather than inherited, which is the entire point of the column. A
 * boundary chained by repeated `+ INTERVAL '1 month'` decays: anchor 31 gives
 * Jan 31 → Feb 28, and the next step reads Feb 28 and yields Mar 28, so the org
 * silently walks backwards down the calendar. Reading the anchor afresh every
 * period makes February a CLAMP rather than a new anchor, so March returns to
 * the 31st.
 *
 * `NULL` means "this org has never been anchored" — a row created after the
 * backfill by one of `billing_period_start`'s writers, none of which set the
 * anchor. Such an org self-anchors on its own start's day of month, which is
 * the same answer anchoring it explicitly would have given, so no writer has to
 * learn about this column for a new org to bill correctly.
 */
export const resolveAnchorDay = (
	periodStart: Date,
	anchorDay: number | null | undefined,
): number => anchorDay ?? periodStart.getUTCDate();

/**
 * Days in a UTC month. `monthIndex` may sit outside 0–11 and is normalised the
 * way `Date.UTC` normalises it, so a caller may pass `month + 1` at December
 * without special-casing the year rollover.
 */
export const daysInUtcMonth = (year: number, monthIndex: number): number =>
	new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/**
 * The anchor's occurrence within ONE given month, at `from`'s exact time of day.
 *
 * `Math.min` against the month's length is the clamp, and it is written here
 * rather than left to `Date.UTC` because `Date.UTC` OVERFLOWS instead: asking it
 * for the 31st of February yields March 3rd. Postgres clamps, we clamp, and that
 * agreement is what `verify-billing-anchor-parity.ts` checks against a live
 * database.
 */
const anchorOccurrence = (
	from: Date,
	year: number,
	monthIndex: number,
	anchorDay: number,
): Date =>
	new Date(
		Date.UTC(
			year,
			monthIndex,
			Math.min(anchorDay, daysInUtcMonth(year, monthIndex)),
			from.getUTCHours(),
			from.getUTCMinutes(),
			from.getUTCSeconds(),
			from.getUTCMilliseconds(),
		),
	);

/**
 * WHERE A BILLING PERIOD ENDS: the next occurrence of the org's anchor day
 * strictly after `periodStart`, carrying `periodStart`'s time of day.
 *
 * THE ONE definition, shared by every TypeScript surface that projects a next
 * close. Its SQL twin is `periodEndExpr` in `api/util/billing-cycle-sql.ts`; the
 * two are held identical by a parity script rather than by hope, because a
 * disagreement between them means the date a customer is shown is not the date
 * they are billed on.
 *
 * ENTIRELY IN UTC, deliberately. Local-time month arithmetic gains or loses an
 * hour across a DST transition, which over a year walks the anniversary off its
 * own instant — the exact drift this whole change exists to stop. It is also why
 * `date-fns` is not used here despite being a dependency: it is local-time.
 *
 * "Strictly after" is what makes the chain safe. A period that starts ON the
 * anchor ends one month later, never instantly, so `period_end` of period N is
 * always `> period_start` of period N and equals `period_start` of period N+1 —
 * no gap to lose revenue in, no overlap to bill twice.
 *
 * An org whose start is off its anchor (a freeze/unfreeze, a mid-cycle Stripe
 * write, or the very first close after this shipped) gets ONE odd-length period
 * and then sits on the anchor forever. That transition is intended; nothing
 * re-stamps `billing_period_start` to force it.
 */
export const billingPeriodEnd = (
	periodStart: Date,
	anchorDay: number | null | undefined,
): Date => {
	const anchor = resolveAnchorDay(periodStart, anchorDay);
	const year = periodStart.getUTCFullYear();
	const month = periodStart.getUTCMonth();
	const thisMonth = anchorOccurrence(periodStart, year, month, anchor);
	return thisMonth.getTime() > periodStart.getTime()
		? thisMonth
		: anchorOccurrence(periodStart, year, month + 1, anchor);
};

/**
 * WHAT A PERIOD IS PRICED AGAINST: the length, in days, of the anniversary month
 * the period sits in — from the anchor occurrence that opens that month to the
 * one that closes it (which is `periodEnd` itself).
 *
 * THE DIVISOR IS THE MONTH, NOT THE PERIOD. That distinction is the entire
 * reason this function exists, and getting it wrong cost real money in both
 * directions:
 *
 *   - Divide by a fixed 30 and a full 31-day period bills 103% of a month while
 *     a full February bills 93%. That was the state before the anniversary
 *     boundary shipped.
 *   - Divide by the period's OWN span and the ratio is 1.0 at any length,
 *     because a period with no billing events is one segment running the whole
 *     period. A 2-day period then bills a FULL MONTH. Short periods are routine
 *     — the transition period after the boundary change, every unfreeze, every
 *     plan change that starts a new period — so this was not a corner case: a
 *     17-day post-unfreeze period billed EUR 229.90 instead of EUR 126.07.
 *
 * Dividing by the containing month gets both right at once: a full period is
 * `monthDays / monthDays` = exactly one month's price whatever the calendar
 * says, and a short one is honestly its fraction of the month it fell in.
 *
 * ## Why `periodStart` is a parameter when only `periodEnd` is measured from
 *
 * The anchor is resolved against the START, exactly as `billingPeriodEnd` does,
 * and that is load-bearing rather than tidiness.
 *
 * IT DIVERGES ONLY WHEN THE ANCHOR IS NULL. For a stored anchor the two
 * readings return the same number — `resolveAnchorDay` hands back the stored
 * value whichever instant it is asked about — so nothing below is visible on an
 * anchored org. That is exactly why this is worth writing down: the parameter
 * looks droppable, and dropping it breaks only the unanchored case.
 *
 * On the NULL fallback the anchor is derived from whichever instant is asked,
 * and start and end disagree. An unanchored org starting 29 Jan 2027 closes
 * 28 Feb — a 30-day period. Resolving from the END self-anchors on 28, measures
 * the month as 28 Jan - 28 Feb (31 days), and bills that FULL period at 30/31 =
 * 96.77% of a month. Resolving from the START gives anchor 29, the month
 * 29 Jan - 28 Feb (30 days), and the ratio is 1.0.
 *
 * Thirteen or fourteen full periods per two years land on that discrepancy —
 * the count is not a rate and moves with where the leap year falls; both values
 * appear across six consecutive year-pairs, so treat it as a handful per year
 * rather than a constant. All of them are on the NULL path and none on a stored
 * anchor. The worst is a 31 Jan start: 28 days against 31, or 90.32%. It is
 * asserted in the tests rather than left to be rediscovered.
 *
 * ## Why it steps back from `periodEnd` rather than forward from `periodStart`
 *
 * `periodEnd` is ALWAYS an anchor occurrence — that is what `billingPeriodEnd`
 * returns — whereas `periodStart` need not be one (a transition or post-unfreeze
 * period starts mid-month). Stepping back one month from a point known to sit on
 * the anchor is therefore total; stepping forward from the start is not. Two
 * properties follow, both proved over every (anchor, start) pair of a leap and a
 * common year:
 *
 *   - ROUND TRIP: `billingPeriodEnd(monthStart, anchor) === periodEnd`. The
 *     month this measures is the same month the boundary would have produced.
 *   - CONTAINMENT: `monthStart <= periodStart < periodEnd`, so the period always
 *     sits inside the month it is priced against and the ratio is always in
 *     (0, 1]. A period can never bill more than a month.
 *
 * ## The 28-31 range is a PRECONDITION, not an invariant — and is not guarded
 *
 * Both properties above, and the 28-31 range they rest on, hold only when
 * `periodEnd` came from `billingPeriodEnd` with the SAME anchor. Handed an
 * arbitrary `periodEnd` this returns anywhere in 1-61 days and says nothing
 * about it: `billingMonthDays(1 Feb, 1 Mar, anchor 31)` is 1, and
 * `(1 Feb, 15 Feb, anchor 31)` is 15. What IS unconditional is that the result
 * is a whole number of days and never zero or negative — `monthStart` always
 * lands in the calendar month before `periodEnd`'s and carries its exact time of
 * day (15 351 adversarial triples: min 1, max 61, no zero, no negative, no
 * fraction).
 *
 * DELIBERATELY UNGUARDED. This runs inside the nightly billing worker, where a
 * throw does not surface a bad argument — it fails the run and every org after
 * the one that tripped it goes unbilled until someone notices. A wrong divisor
 * is recoverable from `proration_days`; a cron that stopped closing periods is
 * discovered by a customer. The precondition instead holds STRUCTURALLY at the
 * only production call site: `processOrgRollover` reads `period_end` and
 * `billing_anchor_day` from the same `orgs` row in the same transaction, so the
 * boundary and the anchor cannot come from two different reads. A future caller
 * that cannot make that guarantee should compute `periodEnd` with
 * `billingPeriodEnd` rather than add a check here.
 */
export const billingMonthDays = (
	periodStart: Date,
	periodEnd: Date,
	anchorDay: number | null | undefined,
): number => {
	const anchor = resolveAnchorDay(periodStart, anchorDay);
	const monthStart = anchorOccurrence(
		periodEnd,
		periodEnd.getUTCFullYear(),
		// `- 1` at January is normalised by `Date.UTC` into December of the
		// previous year, the same way `+ 1` is at December above.
		periodEnd.getUTCMonth() - 1,
		anchor,
	);
	return (periodEnd.getTime() - monthStart.getTime()) / 86_400_000;
};

/** A `YYYY-MM-DD` day as the instant it starts on the UTC clock. */
export const utcMidnight = (day: string): Date =>
	new Date(`${day}T00:00:00.000Z`);

/** The calendar day before a `YYYY-MM-DD` day, on the UTC calendar. */
export const dayBefore = (day: string): string =>
	new Date(utcMidnight(day).getTime() - 86_400_000).toISOString().slice(0, 10);

/** The calendar day after a `YYYY-MM-DD` day, on the UTC calendar. */
export const dayAfter = (day: string): string =>
	new Date(utcMidnight(day).getTime() + 86_400_000).toISOString().slice(0, 10);

/** A moment's calendar day on the UTC clock, `YYYY-MM-DD`. */
export const utcDay = (instant: Date): string =>
	instant.toISOString().slice(0, 10);

/**
 * THE USAGE DAYS A BILLING PERIOD OWNS: from the UTC day it starts up to, NOT
 * including, the UTC day it ends — `[first, end)`.
 *
 * `llm_usage` keeps one row per day, so a bill can only own whole days, and this is
 * the one rule for which ones. Consecutive periods tile: the day a period changes
 * over belongs whole to the period it opens. That is the same day the ERP row prints
 * as the next period's `service_period_from`, so the usage a bill charges and the
 * dates it prints describe the same days. A period that starts and ends on the same
 * day owns none — its day belongs to the period after it.
 *
 * Computed on the UTC clock in code and handed to SQL as plain dates, so the
 * database session's time zone can never move a day.
 *
 * Spelled a second time, in SQL, for reads across many orgs at once:
 * `openPeriodUsagePredicate` in `api/util/billing-cycle-sql.ts`. A change here changes it.
 */
export const usageDays = (
	periodStart: Date,
	periodEnd: Date,
): { first: string; end: string } => ({
	first: utcDay(periodStart),
	end: utcDay(periodEnd),
});

/**
 * The service period's LAST day, inclusive — "28 July – 27 August", as the partner reads
 * a period, rather than the exclusive boundary the report stores.
 *
 * One calendar day before the end boundary's UTC date, and never before the first day.
 * Not `periodEnd − 1 ms`: a period boundary keeps the time of day the org signed up at,
 * so for any anchor not exactly at midnight UTC that lands on the boundary's own date
 * and the next row's first day would repeat it. Consecutive periods tile with this:
 * 28 Jul – 27 Aug, then 28 Aug – 27 Sep.
 *
 * The day before `usageDays`' exclusive `end`: the ERP row prints it, and the dashboard
 * labels a bill with it, so every surface names the same last day. The partner's ERP
 * download selects its bills by this same day too: a file "from 1 Sep to 30 Sep" holds
 * exactly the rows that PRINT a `service_period_to` in September, so what the partner
 * asked for and what the rows say cannot disagree.
 */
export const lastServiceDay = (
	periodStart: string,
	periodEnd: string,
): string => {
	const first = periodStart.slice(0, 10);
	const dayBeforeEnd = dayBefore(periodEnd.slice(0, 10));
	// biome-ignore lint/complexity/useMathMinMax: ISO date strings; Math.max would return NaN
	return dayBeforeEnd < first ? first : dayBeforeEnd;
};

/**
 * A period's days as every surface names them: the usage days `[firstDay, endDay)` its
 * bill counts (`usageDays`) and the last day the bill prints (`lastServiceDay`).
 */
export const periodDays = (
	start: Date,
	end: Date,
): { firstDay: string; endDay: string; lastDay: string } => {
	const days = usageDays(start, end);
	return {
		firstDay: days.first,
		endDay: days.end,
		lastDay: lastServiceDay(start.toISOString(), end.toISOString()),
	};
};

/**
 * How much of the current billing period is still ahead, as a fraction of its whole span.
 * `null` when the organisation has no period, or the period has already closed.
 *
 * Extracted because two callers had grown their own copy of this arithmetic —
 * `prorateCredit` and `proratePages`. Two copies of a money calculation is exactly the
 * divergence Rule #0 exists to prevent: one
 * would eventually be fixed and the others left behind, and the symptom would be two grants
 * on the same day disagreeing about how long a month is.
 *
 * ## The denominator is the PERIOD'S span, not the containing month
 *
 * `buildSegment` divides a CHARGE by the anniversary month the period sits in, so a 17-day
 * stub inside a 31-day month is billed 17/31. This divides a GRANT by the period's own
 * span, so the same stub tops up 17/17 of the remaining fraction. The two are answering
 * different questions: a credit pool is granted per PERIOD and reset whole at every close
 * regardless of how long that period ran.
 *
 * The consequence is real and was accepted rather than overlooked — on a short stub the
 * charge is small while the grant stays large, so the organisation receives proportionally
 * more credit than it paid for.
 *
 * ## Partner organisations
 *
 * They have no Stripe subscription, so `activeUntil` is NULL and Stripe owns no boundary
 * for them; the partner-billing cron does, at the BILLING ANNIVERSARY. Hence the fallback
 * to `billingPeriodEnd` rather than a hardcoded thirty days — once a boundary became a
 * calendar anniversary a period runs 28-31 days, and a fixed 30 over-grants in February and
 * under-grants in a 31-day month.
 */
export const remainingPeriodFraction = (
	org: {
		billingPeriodStart: Date | null;
		billingAnchorDay: number | null;
		activeUntil: Date | null;
	},
	now: Date = new Date(),
): number | null => {
	if (org.billingPeriodStart === null) {
		return null;
	}
	const activeUntil =
		org.activeUntil ??
		billingPeriodEnd(org.billingPeriodStart, org.billingAnchorDay);
	const totalDays =
		(activeUntil.getTime() - org.billingPeriodStart.getTime()) / 86_400_000;
	const remainingDays = Math.max(
		0,
		(activeUntil.getTime() - now.getTime()) / 86_400_000,
	);
	if (totalDays <= 0 || remainingDays <= 0) {
		return null;
	}
	return remainingDays / totalDays;
};
